import Head from 'next/head';
import useTranslation from 'next-translate/useTranslation';

import {
  MARKY_WEBSITE_FAQS,
  MARKY_WEBSITE_LAST_REVIEWED,
  MarkyWebsiteUseCase,
  markyWebsiteUseCases,
} from '@/constants/markyWebsiteUseCases';
import {
  translateMarkyWebsiteFaqs,
  translateMarkyWebsiteText,
  translateMarkyWebsiteUseCase,
} from '@/utils/markyWebsiteTranslation';

const SITE_URL = 'https://easy-peasy.ai';
const HUB_PATH = '/marky/website-builder';

type MarkyWebsiteJsonLdProps = {
  useCase?: MarkyWebsiteUseCase;
};

const faqSchema = (faqs: Array<{ question: string; answer: string }>) => ({
  '@type': 'FAQPage',
  mainEntity: faqs.map((faq) => ({
    '@type': 'Question',
    name: faq.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: faq.answer,
    },
  })),
});

export default function MarkyWebsiteJsonLd({
  useCase,
}: MarkyWebsiteJsonLdProps) {
  const { t, lang } = useTranslation('marky-websites');
  const tr = (value: string) => translateMarkyWebsiteText(t, value);
  const localizedUseCase = useCase
    ? translateMarkyWebsiteUseCase(t, useCase)
    : undefined;
  const localizedUseCases = markyWebsiteUseCases.map((item) =>
    translateMarkyWebsiteUseCase(t, item),
  );
  const hubUrl = `${SITE_URL}${HUB_PATH}`;
  const pageUrl = localizedUseCase
    ? `${hubUrl}/${localizedUseCase.slug}`
    : hubUrl;
  const pageTitle = localizedUseCase
    ? localizedUseCase.seoTitle
    : tr('Marky full-stack AI website builder');
  const pageDescription = localizedUseCase
    ? localizedUseCase.metaDescription
    : tr(
        'Build websites and full-stack web apps with Marky using React, Postgres, authentication, file storage, GitHub, custom domains, and Google Cloud Run.',
      );

  const breadcrumbItems = [
    {
      '@type': 'ListItem',
      position: 1,
      name: 'Easy-Peasy.AI',
      item: SITE_URL,
    },
    {
      '@type': 'ListItem',
      position: 2,
      name: tr('Marky Agent'),
      item: `${SITE_URL}/marky`,
    },
    {
      '@type': 'ListItem',
      position: 3,
      name: tr('AI Website Builder'),
      item: hubUrl,
    },
  ];

  if (localizedUseCase) {
    breadcrumbItems.push({
      '@type': 'ListItem',
      position: 4,
      name: localizedUseCase.name,
      item: pageUrl,
    });
  }

  const graph: Array<Record<string, unknown>> = [
    {
      '@type': 'WebPage',
      '@id': `${pageUrl}#webpage`,
      url: pageUrl,
      name: pageTitle,
      description: pageDescription,
      inLanguage: lang,
      dateModified: MARKY_WEBSITE_LAST_REVIEWED,
      isPartOf: {
        '@type': 'WebSite',
        '@id': `${SITE_URL}/#website`,
        name: 'Easy-Peasy.AI',
        url: SITE_URL,
      },
      about: {
        '@type': 'SoftwareApplication',
        name: 'Marky Agent',
        applicationCategory: 'DeveloperApplication',
        operatingSystem: 'Web',
        url: `${SITE_URL}/marky`,
      },
    },
    {
      '@type': 'BreadcrumbList',
      itemListElement: breadcrumbItems,
    },
    faqSchema(
      localizedUseCase
        ? localizedUseCase.faqs
        : translateMarkyWebsiteFaqs(t, MARKY_WEBSITE_FAQS),
    ),
  ];

  if (localizedUseCase) {
    graph.push({
      '@type': 'HowTo',
      name: t(
        'How to build {{projectType}} with Marky',
        { projectType: localizedUseCase.name },
        { default: `How to build ${localizedUseCase.name} with Marky` },
      ),
      description: localizedUseCase.definition,
      step: localizedUseCase.buildSteps.map((step, index) => ({
        '@type': 'HowToStep',
        position: index + 1,
        name: step.title,
        text: step.description,
      })),
    });
  } else {
    graph.push({
      '@type': 'ItemList',
      name: tr('Website and web app types Marky can build'),
      numberOfItems: localizedUseCases.length,
      itemListElement: localizedUseCases.map((item, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: item.name,
        url: `${hubUrl}/${item.slug}`,
      })),
    });
  }

  const structuredData = {
    '@context': 'https://schema.org',
    '@graph': graph,
  };

  return (
    <Head>
      <script
        id={
          localizedUseCase
            ? `marky-website-${localizedUseCase.slug}-schema`
            : 'marky-websites-schema'
        }
        type='application/ld+json'
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(structuredData).replace(/</g, '\\u003c'),
        }}
      />
    </Head>
  );
}
